import React from 'react'
import { useAppSelector } from '../redux/hooks'
import User, { RoleType } from '../models/User'
import { selectUserData } from '../redux/actions/login/loginSlice'
import { usePermissions } from '../hooks/usePermissions'
import { AdminPermission, guardRoute } from './adminRouteHelpers'
import { getFirstAllowedRoute, resolveAdminPanelPath } from '../utils/permissionUtil'
import { Navigate } from 'react-router-dom'
import { LOTUS_ADMIN_DEFAULT_ROUTE, LOTUS_ADMIN_PAGES } from './pages/_layout/elements/lotusAdminPages'

const ActiveMarkets = React.lazy(() => import('../admin-app/pages/active-matches/active-markets'))
const ActiveMatches = React.lazy(() => import('../admin-app/pages/active-matches/active-matches'))
const GetAllFancy = React.lazy(() => import('../admin-app/pages/active-matches/get-all-fancy'))
const MatchesPage = React.lazy(() => import('../admin-app/pages/add-matches/matches'))
const SeriesPage = React.lazy(() => import('../admin-app/pages/add-matches/series'))
const SportsPage = React.lazy(() => import('../admin-app/pages/add-matches/sports'))
const AddUser = React.lazy(() => import('../admin-app/pages/add-user/add-user'))
const ListClients = React.lazy(() => import('../admin-app/pages/list-clients/list-clients'))
const MainAdmin = React.lazy(() => import('../admin-app/pages/_layout/MainAdmin'))
const Message = React.lazy(() => import('../admin-app/pages/settings/message'))
const BroadcastNotifications = React.lazy(
  () => import('../admin-app/pages/settings/broadcast-notifications'),
)
const WhatsAppSettings = React.lazy(() => import('../admin-app/pages/settings/whatsapp-settings'))
const Paymethod = React.lazy(() => import('../admin-app/pages/settings/paymethod'))
const UndeclaredBets = React.lazy(() => import('../admin-app/pages/undeclared-bets/undeclared-bets'))
const SeriesSettings = React.lazy(() => import('../admin-app/pages/settings/series-settings'))
const MatchSetting = React.lazy(() => import('../admin-app/pages/match-setting/match-setting'))
const AdminDashboard = React.lazy(
  () => import('../admin-app/pages/admin-dashboard/admin-dashboard'),
)
const MarketAnalysis = React.lazy(
  () => import('../admin-app/pages/market-analysis/MarketAnalysis'),
)
const FinancialDashboard = React.lazy(
  () => import('../admin-app/pages/financial-dashboard/financial-dashboard'),
)
const AccountStatementAdmin = React.lazy(
  () => import('../admin-app/pages/AccountStatement/AccountStatementAdmin'),
)
const DepositStatementAdmin = React.lazy(
  () => import('../admin-app/pages/transaction-statement/DepositStatementAdmin'),
)
const WithdrawStatementAdmin = React.lazy(
  () => import('../admin-app/pages/transaction-statement/WithdrawStatementAdmin'))
const ProfitLossAdmin = React.lazy(() => import('../admin-app/pages/PlReport/ProfitLossAdmin'))
const UnsetteleBetHistoryAdmin = React.lazy(
  () => import('../admin-app/pages/UnsetteleBetHistory/UnsetteleBetHistoryAdmin'),
)
const Odds = React.lazy(() => import('../pages/odds/odds'))
const AdminChangePasswordPage = React.lazy(
  () => import('./pages/change-password/AdminChangePasswordPage'),
)
const AuthLayout = React.lazy(() => import('../pages/_layout/AuthLayout'))
const Login = React.lazy(() => import('./pages/login/login'))
const CasinoWrapper = React.lazy(() => import('../pages/CasinoList/CasinoWrapper'))
const CasinoList = React.lazy(() => import('./pages/casino-list/casino-list'))
const GameReportAdmin = React.lazy(() => import('./pages/GameReports/GameReportAdmin'))
const LedgerAdmin = React.lazy(() => import('../admin-app/pages/Ledger/LedgerAdmin'))
const UserBookAdmin = React.lazy(() => import('../admin-app/pages/UserBook/UserBookAdmin'))
const Rules = React.lazy(() => import('./pages/Rules/rules'))
const Tc = React.lazy(() => import('./pages/Rules/tc'))
const AnalysisProfitloss = React.lazy(() => import('./pages/Anaysis-pl/AnalysisProfitloss'))
const InplayMatch = React.lazy(() => import('./pages/InplayMatch/InplayMatch'))
const SecurityMonitoring = React.lazy(() => import('./pages/security-monitoring/SecurityMonitoring'))
const BetDelay = React.lazy(() => import('./pages/settings/bet-delay'))
const CasinoVisibility = React.lazy(() => import('./pages/casino-visibility/CasinoVisibility'))
const AffiliateManagement = React.lazy(() => import('./pages/affiliate-management/AffiliateManagement'))
const BonusAdmin = React.lazy(() => import('./pages/bonus/BonusAdmin'))
const PaymentSettings = React.lazy(() => import('./pages/settings/payment-settings/PaymentSettings'))
const RbacUsers = React.lazy(() => import('./pages/rbac-users/RbacUsers'))
const Unauthorized = React.lazy(() => import('./pages/unauthorized/Unauthorized'))
const LotusAdminBlankPage = React.lazy(() => import('./pages/lotus/LotusAdminBlankPage'))
const MemberListingPage = React.lazy(() => import('./pages/member-listing/MemberListingPage'))
const CreateMemberPage = React.lazy(() => import('./pages/member-listing/CreateMemberPage'))
const MemberReportPage = React.lazy(() => import('./pages/member-listing/MemberReportPage'))
const PositionTakingPage = React.lazy(() => import('./pages/position-taking/PositionTakingPage'))
const TransferPage = React.lazy(() => import('./pages/transfer/TransferPage'))
const NetExposurePage = React.lazy(() => import('./pages/risk-management/net-exposure/NetExposurePage'))
const BetTickerPage = React.lazy(() => import('./pages/risk-management/bet-ticker/BetTickerPage'))
const EventManagementPage = React.lazy(
  () => import('./pages/risk-management/event-management/EventManagementPage'),
)
const PlReportMarketPage = React.lazy(
  () => import('./pages/reports/pl-report-market/PlReportMarketPage'),
)
const PlReportAgentPage = React.lazy(
  () => import('./pages/reports/pl-report-agent/PlReportAgentPage'),
)
const CasinoReportPage = React.lazy(
  () => import('./pages/reports/casino-report/CasinoReportPage'),
)
const BetListPage = React.lazy(() => import('./pages/reports/bet-list/BetListPage'))
const TransferStatementPage = React.lazy(
  () => import('./pages/reports/transfer-statement/TransferStatementPage'),
)
const BalancePage = React.lazy(() => import('./pages/account/balance/BalancePage'))
const StatementPage = React.lazy(() => import('./pages/account/statement/StatementPage'))
const UserListingPage = React.lazy(() => import('./pages/user-listing/UserListingPage'))
const CreateUserPage = React.lazy(() => import('./pages/user-listing/CreateUserPage'))

const lotusAdminRoutes = LOTUS_ADMIN_PAGES.filter(
  (page) =>
    page.id !== 'member-listing' &&
    page.id !== 'position-taking-listing' &&
    page.id !== 'transfer-agency' &&
    page.id !== 'net-exposure' &&
    page.id !== 'bet-ticker' &&
    page.id !== 'event-management' &&
    page.id !== 'pl-report-market' &&
    page.id !== 'pl-report-agent' &&
    page.id !== 'casino-report' &&
    page.id !== 'bet-list' &&
    page.id !== 'transfer-statement' &&
    page.id !== 'balance' &&
    page.id !== 'statement' &&
    page.id !== 'user-listing' &&
    page.id !== 'notifications' &&
    page.id !== 'undeclared-bets' &&
    page.id !== 'market-analysis',
).map((page) => ({
  path: page.path,
  element: <LotusAdminBlankPage pageId={page.id} />,
}))

const lotusAdditionalManagementRoutes = [
  { path: 'broadcast-notifications', element: <BroadcastNotifications /> },
  { path: 'undeclared-bets', element: <UndeclaredBets /> },
  { path: 'market-analysis', element: <MarketAnalysis /> },
]

const lotusMemberListingRoutes = [
  { path: 'member-listing', element: <MemberListingPage /> },
  { path: 'member-listing/create', element: <CreateMemberPage /> },
  { path: 'member-listing/create/:parentLogin', element: <CreateMemberPage /> },
  { path: 'member-listing/:parentLogin', element: <MemberListingPage /> },
  { path: 'member-report/:userCode/:loginName/:tab?', element: <MemberReportPage /> },
]

const lotusPositionTakingRoutes = [
  { path: 'position-taking-listing', element: <PositionTakingPage /> },
  { path: 'position-taking-listing/:parentLogin', element: <PositionTakingPage /> },
]

const lotusTransferRoutes = [{ path: 'transfer', element: <TransferPage /> }]

const lotusAccountRoutes = [
  { path: 'balance', element: <BalancePage /> },
  { path: 'statement', element: <StatementPage /> },
]

const lotusAdminUserRoutes = [
  { path: 'user-listing', element: <UserListingPage /> },
  { path: 'user-listing/create', element: <CreateUserPage /> },
]

const lotusRiskRoutes = [
  { path: 'net-exposure', element: <NetExposurePage /> },
  { path: 'bet-ticker', element: <BetTickerPage /> },
  { path: 'lotus-event-management', element: <EventManagementPage /> },
  { path: 'pl-report-market', element: <PlReportMarketPage /> },
  { path: 'pl-report-agent', element: <PlReportAgentPage /> },
  { path: 'casino-report', element: <CasinoReportPage /> },
  { path: 'bet-list', element: <BetListPage /> },
  { path: 'transfer-statement', element: <TransferStatementPage /> },
]

const RbacHomeRedirect = () => {
  const userState = useAppSelector<{ user: User }>(selectUserData)
  return <Navigate to={resolveAdminPanelPath(getFirstAllowedRoute(userState.user))} replace />
}

const AdminHome = () => {
  return <Navigate to={`/admin/${LOTUS_ADMIN_DEFAULT_ROUTE}`} replace />
}

const AdminRoutes = () => {
  const userState = useAppSelector<{ user: User }>(selectUserData)
  const { hasPermission, isFullAdmin } = usePermissions()
  const can = (p: keyof typeof AdminPermission) =>
    isFullAdmin || hasPermission(AdminPermission[p])

  const superAdminRoutes =
    userState.user.role === RoleType.admin || userState.user.role === RoleType.sadmin
      ? [
          { path: 'sports-list/:url?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <SportsPage />) },
          { path: 'series/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <SeriesPage />) },
          { path: 'matches/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <MatchesPage />) },
          { path: 'active-matches/:sportId/:matchType?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMatches />) },
          { path: 'active-markets/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMarkets />) },
          { path: 'active-fancies/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <GetAllFancy />) },
          { path: 'messages', element: guardRoute(AdminPermission.MESSAGES, <Message />) },
          { path: 'whatsapp-settings', element: guardRoute(AdminPermission.WHATSAPP, <WhatsAppSettings />) },
          { path: 'series-settings', element: guardRoute(AdminPermission.SERIES_SETTINGS, <SeriesSettings />) },
          { path: 'betdelay', element: guardRoute(AdminPermission.BET_DELAY, <BetDelay />) },
        ]
      : []

  const rbacSettingsRoutes = isFullAdmin
    ? []
    : [
        ...(can('BLOCK_MARKETS')
          ? [
              { path: 'sports-list/:url?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <SportsPage />) },
              { path: 'active-matches/:sportId/:matchType?', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMatches />) },
              { path: 'active-markets/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <ActiveMarkets />) },
              { path: 'active-fancies/:matchId', element: guardRoute(AdminPermission.BLOCK_MARKETS, <GetAllFancy />) },
            ]
          : []),
        ...(can('ADD_MATCH')
          ? [
              { path: 'series/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <SeriesPage />) },
              { path: 'matches/:sportId', element: guardRoute(AdminPermission.ADD_MATCH, <MatchesPage />) },
            ]
          : []),
        ...(can('MESSAGES')
          ? [{ path: 'messages', element: guardRoute(AdminPermission.MESSAGES, <Message />) }]
          : []),
        ...(can('WHATSAPP')
          ? [{ path: 'whatsapp-settings', element: guardRoute(AdminPermission.WHATSAPP, <WhatsAppSettings />) }]
          : []),
        ...(can('SERIES_SETTINGS')
          ? [{ path: 'series-settings', element: guardRoute(AdminPermission.SERIES_SETTINGS, <SeriesSettings />) }]
          : []),
        ...(can('BET_DELAY') ? [{ path: 'betdelay', element: guardRoute(AdminPermission.BET_DELAY, <BetDelay />) }] : []),
        ...(can('MATCH_SETTINGS')
          ? [{ path: 'match-settings/:matchId', element: guardRoute(AdminPermission.MATCH_SETTINGS, <MatchSetting />) }]
          : []),
      ]

  return [
    {
      path: '/admin',
      element: <AuthLayout />,
      children: [
        { path: 'login', element: <Login /> },
        {
          path: '',
          element: <MainAdmin />,
          children: [
            {
              index: true,
              element: <AdminHome />,
            },
            ...lotusMemberListingRoutes,
            ...lotusPositionTakingRoutes,
            ...lotusTransferRoutes,
            ...lotusRiskRoutes,
            ...lotusAccountRoutes,
            ...lotusAdminUserRoutes,
            ...(userState.user.role === RoleType.admin ? lotusAdditionalManagementRoutes : []),
            ...lotusAdminRoutes,
            { path: 'financial-dashboard', element: <FinancialDashboard /> },
            { path: 'unauthorized', element: <Unauthorized /> },
            ...(isFullAdmin
              ? [{ path: 'rbac-users', element: guardRoute(AdminPermission.RBAC_MANAGE, <RbacUsers />) }]
              : []),
            ...(can('EVENT_MANAGEMENT')
              ? [
                  { path: 'event-management', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <InplayMatch />) },
                  { path: 'dashbaord', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <AdminDashboard />) },
                  { path: 'inplay-matches', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <InplayMatch />) },
                  { path: 'odds/:matchId', element: guardRoute(AdminPermission.EVENT_MANAGEMENT, <Odds />) },
                ]
              : []),
            ...(can('USERS')
              ? [
                  ...['list-clients', 'list-clients/:username'].map((path) => ({
                    key: 'list-client',
                    path,
                    element: guardRoute(AdminPermission.USERS, <ListClients />),
                  })),
                  ...['add-user', 'add-user/:username'].map((path) => ({
                    key: 'add-user',
                    path,
                    element: guardRoute(AdminPermission.USERS, <AddUser />),
                  })),
                ]
              : []),
            { path: 'rules', element: <Rules /> },
            { path: 'terms', element: <Tc /> },
            ...superAdminRoutes,
            ...rbacSettingsRoutes,
            ...(can('SECURITY_MONITORING')
              ? [{ path: 'security-monitoring', element: guardRoute(AdminPermission.SECURITY_MONITORING, <SecurityMonitoring />) }]
              : userState.user.role !== RoleType.user
                ? []
                : []),
            { path: 'change-password', element: <AdminChangePasswordPage /> },
            ...(can('ACCOUNT_STATEMENT')
              ? [{ path: 'accountstatement', element: guardRoute(AdminPermission.ACCOUNT_STATEMENT, <AccountStatementAdmin />) }]
              : []),
            ...(can('PROFIT_LOSS')
              ? [{ path: 'profitloss', element: guardRoute(AdminPermission.PROFIT_LOSS, <ProfitLossAdmin />) }]
              : []),
            ...(can('DELETED_BETS')
              ? [{ path: 'unsettledbet/deleted', element: guardRoute(AdminPermission.DELETED_BETS, <UnsetteleBetHistoryAdmin />) }]
              : []),
            ...(can('CURRENT_BETS')
              ? [
                  { path: 'unsettledbet', element: guardRoute(AdminPermission.CURRENT_BETS, <UnsetteleBetHistoryAdmin />) },
                  { path: 'unsettledbet/:type', element: guardRoute(AdminPermission.CURRENT_BETS, <UnsetteleBetHistoryAdmin />) },
                ]
              : []),
            ...(can('CASINO_LIST')
              ? [
                  { path: 'casino/:gameCode', element: guardRoute(AdminPermission.CASINO_LIST, <CasinoWrapper />) },
                  { path: 'casino-list', element: guardRoute(AdminPermission.CASINO_LIST, <CasinoList />) },
                ]
              : []),
            ...(can('CASINO_VISIBILITY')
              ? [{ path: 'casino-visibility', element: guardRoute(AdminPermission.CASINO_VISIBILITY, <CasinoVisibility />) }]
              : []),
            ...(can('GAME_REPORTS')
              ? [{ path: 'game-reports', element: guardRoute(AdminPermission.GAME_REPORTS, <GameReportAdmin />) }]
              : []),
            ...(can('DEPOSIT')
              ? [{ path: 'depositstatement', element: guardRoute(AdminPermission.DEPOSIT, <DepositStatementAdmin />) }]
              : []),
            ...(can('WITHDRAW')
              ? [{ path: 'withdrawstatement', element: guardRoute(AdminPermission.WITHDRAW, <WithdrawStatementAdmin />) }]
              : []),
            ...(can('PAYMENT_METHOD')
              ? [{ path: 'payment-method', element: guardRoute(AdminPermission.PAYMENT_METHOD, <Paymethod />) }]
              : []),
            ...(can('LEDGER') ? [{ path: 'ledger', element: guardRoute(AdminPermission.LEDGER, <LedgerAdmin />) }] : []),
            ...(can('USER_BOOK')
              ? [{ path: 'user-book', element: guardRoute(AdminPermission.USER_BOOK, <UserBookAdmin />) }]
              : []),
            ...(can('AFFILIATES')
              ? [{ path: 'affiliates', element: guardRoute(AdminPermission.AFFILIATES, <AffiliateManagement />) }]
              : []),
            ...(can('BONUS') ? [{ path: 'bonus', element: guardRoute(AdminPermission.BONUS, <BonusAdmin />) }] : []),
            ...(can('PAYMENT_SETTINGS')
              ? [{ path: 'payment-settings', element: guardRoute(AdminPermission.PAYMENT_SETTINGS, <PaymentSettings />) }]
              : []),
            ...(can('MATCH_SETTINGS')
              ? [{ path: 'match-settings/:matchId', element: guardRoute(AdminPermission.MATCH_SETTINGS, <MatchSetting />) }]
              : []),
            ...(can('ADMIN_PL')
              ? [{ path: 'analysis-pl', element: guardRoute(AdminPermission.ADMIN_PL, <AnalysisProfitloss />) }]
              : []),
          ],
        },
      ],
    },
  ]
}

export default AdminRoutes
